/**
 * LyteWork Cart Count Fix + Customization Options Lock + Orphan Cleanup
 * 
 * 1. Recalculates the cart count badge to exclude the surcharge variant
 * 2. Locks the Customization Options line item from being modified/deleted
 * 3. Removes orphaned surcharge lines when their associated product is deleted
 */
(function() {
    'use strict';

    const SURCHARGE_VARIANT_IDS = [41832906817610];

    const CART_COUNT_SELECTORS = [
        '.cart-count-badge',
        '.cart-count',
        '.cart-counter',
        '.cart-item-count',
        '.cart-icon-count',
        '[data-cart-count]',
        '.header-cart-count',
        '.cart-badge'
    ];

    const CART_LINK_SELECTORS = [
        '.cart-icon',
        '.cart-link',
        'a[href="/cart"]',
        'a[href*="/cart"]'
    ];

    async function fetchCart() {
        try {
            const response = await fetch('/cart.js');
            return await response.json();
        } catch (e) {
            console.error('LyteWork cart fix: failed to fetch cart', e);
            return null;
        }
    }

    function calculateDisplayCount(cart) {
        if (!cart || !cart.items) return 0;
        let count = 0;
        cart.items.forEach(item => {
            if (!SURCHARGE_VARIANT_IDS.includes(item.variant_id)) {
                count += item.quantity;
            }
        });
        return count;
    }

    function updateAllBadges(displayCount) {
        CART_COUNT_SELECTORS.forEach(selector => {
            document.querySelectorAll(selector).forEach(el => {
                el.textContent = displayCount;
                if (displayCount === 0) {
                    el.setAttribute('hidden', 'hidden');
                } else {
                    el.removeAttribute('hidden');
                }
            });
        });

        CART_LINK_SELECTORS.forEach(selector => {
            document.querySelectorAll(selector).forEach(el => {
                const currentLabel = el.getAttribute('aria-label') || '';
                if (currentLabel.toLowerCase().includes('cart')) {
                    el.setAttribute(
                        'aria-label',
                        displayCount === 0 
                            ? 'Cart' 
                            : `Cart, ${displayCount} item${displayCount === 1 ? '' : 's'}`
                    );
                }
            });
        });
    }

    function lockSurchargeLines(cart) {
        if (!cart || !cart.items) return;

        const surchargeKeys = cart.items
            .filter(item => SURCHARGE_VARIANT_IDS.includes(item.variant_id))
            .map(item => item.key);

        if (surchargeKeys.length === 0) return;

        surchargeKeys.forEach(key => {
            const qtyInput = document.querySelector(`input[data-line-item-key="${key}"]`);
            if (!qtyInput) return;

            const productItem = qtyInput.closest('[data-cart-line-item]') 
                              || qtyInput.closest('.product-item');
            if (!productItem) return;

            productItem.classList.add('lw-surcharge-locked');

            qtyInput.disabled = true;
            qtyInput.setAttribute('readonly', 'readonly');
            qtyInput.setAttribute('aria-label', 'Customization quantity (locked)');

            const qtyButtons = productItem.querySelectorAll('button[data-mode="plus"], button[data-mode="minus"]');
            qtyButtons.forEach(btn => {
                btn.disabled = true;
                btn.setAttribute('aria-disabled', 'true');
                btn.style.pointerEvents = 'none';
                btn.style.opacity = '0.4';
            });

            const removeBtn = productItem.querySelector(`button[data-remove-line-item][data-line-item-key="${key}"]`)
                            || productItem.querySelector('button[data-remove-line-item]');
            if (removeBtn) {
                removeBtn.style.display = 'none';
            }
        });
    }

    /**
     * Normalize a product title for fuzzy matching.
     * Strips extra whitespace, quantity annotations like "(5)", and lowercases.
     */
    function normalizeProductTitle(title) {
        if (!title) return '';
        return String(title)
            .toLowerCase()
            .replace(/\s*\(\d+\)\s*$/, '')  // remove "(5)" quantity annotations
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Detect and remove orphaned surcharge line items.
     * 
     * Uses TWO mechanisms in priority order:
     * 
     * 1. PRIMARY: If both the main product and its surcharge carry a matching
     *    "_lw_link_id" property, we match by that ID. This is bulletproof and
     *    handles multiple instances of the same product. Customizers add this
     *    property starting from this version of the code.
     * 
     * 2. FALLBACK: Surcharges from pre-link-id orders use the older "For"
     *    property matching. This handles legacy carts but is less precise
     *    when multiple identical products exist.
     */
    async function removeOrphanedSurcharges(cart) {
        if (!cart || !cart.items || cart.items.length === 0) return false;

        // Separate cart items into surcharges and real products
        const surchargeLines = cart.items.filter(item => 
            SURCHARGE_VARIANT_IDS.includes(item.variant_id)
        );
        const productLines = cart.items.filter(item => 
            !SURCHARGE_VARIANT_IDS.includes(item.variant_id)
        );

        if (surchargeLines.length === 0) return false;

        // Build sets of link IDs and product titles from active products
        const activeLinkIds = new Set();
        const activeProductTitles = new Map(); // title -> count
        
        productLines.forEach(product => {
            const linkId = product.properties && product.properties['_lw_link_id'];
            if (linkId) {
                activeLinkIds.add(linkId);
            }
            const title = normalizeProductTitle(product.product_title || product.title);
            activeProductTitles.set(title, (activeProductTitles.get(title) || 0) + 1);
        });

        // Track how many surcharges we've "consumed" for each title (for fallback matching)
        const titleSurchargeCount = new Map();
        
        const orphans = [];

        surchargeLines.forEach(surcharge => {
            const surchargeLinkId = surcharge.properties && surcharge.properties['_lw_link_id'];
            
            // PRIMARY: Match by link ID if present
            if (surchargeLinkId) {
                if (!activeLinkIds.has(surchargeLinkId)) {
                    console.log(
                        `LyteWork: Orphan surcharge (link ID ${surchargeLinkId}) — ` +
                        `no matching product in cart.`
                    );
                    orphans.push(surcharge);
                }
                return; // Definitive match either way, move on
            }
            
            // FALLBACK: No link ID, use title-based matching with counts
            const forProperty = surcharge.properties && surcharge.properties['For'];
            
            if (!forProperty) {
                // No "For" property AND no link ID — only orphan if cart has zero products
                if (productLines.length === 0) {
                    orphans.push(surcharge);
                }
                return;
            }

            const normalizedFor = normalizeProductTitle(forProperty);
            
            // Find best matching product title
            let matchedTitle = null;
            for (const [productTitle, count] of activeProductTitles) {
                if (productTitle === normalizedFor || 
                    productTitle.includes(normalizedFor) || 
                    normalizedFor.includes(productTitle)) {
                    matchedTitle = productTitle;
                    break;
                }
            }

            if (!matchedTitle) {
                console.log(
                    `LyteWork: Orphan surcharge detected for "${forProperty}" — ` +
                    `no matching product remains in cart.`
                );
                orphans.push(surcharge);
                return;
            }

            // Title matches, but is this surcharge in excess of the product count?
            const consumedCount = titleSurchargeCount.get(matchedTitle) || 0;
            const activeCount = activeProductTitles.get(matchedTitle) || 0;
            
            if (consumedCount >= activeCount) {
                // More surcharges than products with this title — this one is excess
                console.log(
                    `LyteWork: Excess surcharge for "${forProperty}" — ` +
                    `cart has ${activeCount} of that product but ${consumedCount + 1} surcharges.`
                );
                orphans.push(surcharge);
            } else {
                // This surcharge "consumes" one product slot
                titleSurchargeCount.set(matchedTitle, consumedCount + 1);
            }
        });

        if (orphans.length === 0) return false;

        // Remove orphans
        let removalSucceeded = false;
        for (const orphan of orphans) {
            try {
                const response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: orphan.key,
                        quantity: 0
                    })
                });
                if (response.ok) {
                    removalSucceeded = true;
                    console.log(`LyteWork: Removed orphaned surcharge "${orphan.properties?.For || '?'}"`);
                }
            } catch (e) {
                console.error('LyteWork: Failed to remove orphan surcharge', e);
            }
        }

        // If we removed something, the cart has changed — reload to reflect
        if (removalSucceeded) {
            // On the cart page, reload to refresh the UI
            if (window.location.pathname === '/cart') {
                window.location.reload();
            }
        }

        return removalSucceeded;
    }

    async function refresh() {
        const cart = await fetchCart();
        
        // First, check for and remove orphaned surcharges
        const removedOrphans = await removeOrphanedSurcharges(cart);
        
        // If we removed orphans, refetch the cart for accurate counts
        const currentCart = removedOrphans ? await fetchCart() : cart;
        
        const displayCount = calculateDisplayCount(currentCart);
        updateAllBadges(displayCount);
        lockSurchargeLines(currentCart);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', refresh);
    } else {
        refresh();
    }

    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:refresh', refresh);

    // Re-run after any DOM mutation that adds/removes cart line items
    const cartContainer = document.querySelector('#cart, .cart, [data-cart-page]');
    if (cartContainer) {
        const observer = new MutationObserver(() => {
            clearTimeout(window._lwLockTimer);
            window._lwLockTimer = setTimeout(refresh, 200);
        });
        observer.observe(cartContainer, { childList: true, subtree: true });
    }

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        const isCartMod = url.includes('/cart/add') 
            || url.includes('/cart/update') 
            || url.includes('/cart/change');

        const promise = originalFetch.apply(window, args);
        if (isCartMod) {
            promise.then(() => setTimeout(refresh, 300));
        }
        return promise;
    };
})();